import * as z from 'zod'
import { postgrestFrom, runPostgrest } from '@/api/postgrest/client'
import {
  collectionAggregateRowSchema,
  collectionKeywordRowSchema,
  collectionMasterRowSchema,
  collectionSalesRowSchema,
  subCollectionRowSchema,
} from '@/api/collectionKeywords/schemas'
import type {
  CollectionAggregateRow,
  CollectionKeywordDetail,
  CollectionKeywordRow,
  CollectionKeywordSummary,
  CollectionMasterRow,
  CollectionMetric,
  CollectionSalesRow,
  SubCollectionRow,
} from '@/api/collectionKeywords/types'
import type { KeywordAssignment } from '@/api/keywordAnalysis/types'

const QUERY_PAGE_SIZE = 500
const metrics = ['total', 'primary', 'secondary', 'emotion', 'longtail', 'alias'] as const

async function fetchAllCollections(signal?: AbortSignal): Promise<readonly CollectionMasterRow[]> {
  const rows: CollectionMasterRow[] = []
  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('collections')
      .select(
        'collectionId:collections_id,collectionName:name,parentId:parent_id,description,aiInstructions:AiInstructions',
      )
      .order('collections_id', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)
    const page = await runPostgrest(query, z.array(collectionMasterRowSchema), 'collections')
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

async function fetchCollectionSales(signal?: AbortSignal): Promise<readonly CollectionSalesRow[]> {
  let query = postgrestFrom('MotiveSales')
    .select(
      'sales365:Sales365.sum(),...motives!inner(directCollectionId:CollectionId,subCollectionId:sub_collection_id)',
    )
    .limit(1000)
  if (signal !== undefined) query = query.abortSignal(signal)
  return runPostgrest(query, z.array(collectionSalesRowSchema), 'MotiveSales je Collection')
}

async function fetchSubCollections(signal?: AbortSignal): Promise<readonly SubCollectionRow[]> {
  const rows: SubCollectionRow[] = []
  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('sub_collection')
      .select('subCollectionId:id,collectionId:collection_id')
      .order('id', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)
    const page = await runPostgrest(query, z.array(subCollectionRowSchema), 'sub_collection')
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

function filterMetric<T extends { eq(column: string, value: unknown): T }>(
  query: T,
  metric: CollectionMetric,
): T {
  switch (metric) {
    case 'primary':
      return query.eq('keyword_filter.keyword_type', 0)
    case 'secondary':
      return query.eq('keyword_filter.keyword_type', 1)
    case 'emotion':
      return query.eq('keyword_filter.keyword_type', 2)
    case 'longtail':
      return query.eq('keyword_filter.keyword_type', 3)
    case 'alias':
      return query.eq('keyword_filter.is_alias', true)
    case 'total':
      return query
  }
}

async function fetchAggregate(
  metric: CollectionMetric,
  kind: 'count' | 'traffic',
  signal?: AbortSignal,
): Promise<readonly CollectionAggregateRow[]> {
  const selection = (() => {
    if (kind === 'count') {
      return metric === 'total'
        ? 'collectionId:collection_id,value:count()'
        : 'collectionId:collection_id,value:count(),keyword_filter:keywords!keyword_id!inner()'
    }
    return metric === 'total'
      ? 'collectionId:collection_id,...keywords!keyword_id!inner(value:search_volume_per_month_avg.sum())'
      : 'collectionId:collection_id,keyword_filter:keywords!keyword_id!inner(),...keywords!keyword_id(value:search_volume_per_month_avg.sum())'
  })()
  let query = postgrestFrom('collection_keywords').select(selection).limit(1000)
  query = filterMetric(query, metric)
  if (signal !== undefined) query = query.abortSignal(signal)
  return runPostgrest(query, z.array(collectionAggregateRowSchema), `collection_keywords ${kind}`)
}

export async function fetchCollectionKeywordSummaries(
  signal?: AbortSignal,
): Promise<readonly CollectionKeywordSummary[]> {
  const [collections, salesRows, subCollections, aggregateGroups] = await Promise.all([
    fetchAllCollections(signal),
    fetchCollectionSales(signal),
    fetchSubCollections(signal),
    Promise.all(
      metrics.flatMap((metric) => [
        fetchAggregate(metric, 'count', signal),
        fetchAggregate(metric, 'traffic', signal),
      ]),
    ),
  ])
  const collectionBySubCollectionId = new Map(
    subCollections.map((row) => [row.subCollectionId, row.collectionId] as const),
  )
  const salesById = new Map<number, number>()
  for (const row of salesRows) {
    const collectionId =
      row.subCollectionId === null
        ? row.directCollectionId
        : (collectionBySubCollectionId.get(row.subCollectionId) ?? row.directCollectionId)
    if (collectionId === null) continue
    salesById.set(collectionId, (salesById.get(collectionId) ?? 0) + row.sales365)
  }
  const namesById = new Map(
    collections.map((row) => [row.collectionId, row.collectionName] as const),
  )
  const aggregateMaps = aggregateGroups.map(
    (rows) => new Map(rows.map((row) => [row.collectionId, row.value] as const)),
  )

  return collections.map((row) => {
    const values = aggregateMaps.map((map) => map.get(row.collectionId) ?? 0)
    return {
      ...row,
      parentName: row.parentId === null ? null : (namesById.get(row.parentId) ?? null),
      sales365: salesById.get(row.collectionId) ?? 0,
      totalCount: values[0] ?? 0,
      totalTraffic: values[1] ?? 0,
      primaryCount: values[2] ?? 0,
      primaryTraffic: values[3] ?? 0,
      secondaryCount: values[4] ?? 0,
      secondaryTraffic: values[5] ?? 0,
      emotionCount: values[6] ?? 0,
      emotionTraffic: values[7] ?? 0,
      longtailCount: values[8] ?? 0,
      longtailTraffic: values[9] ?? 0,
      aliasCount: values[10] ?? 0,
      aliasTraffic: values[11] ?? 0,
    }
  })
}

export async function fetchCollectionKeywords(
  collectionIds: readonly number[],
  signal?: AbortSignal,
): Promise<readonly CollectionKeywordRow[]> {
  const rows: CollectionKeywordRow[] = []
  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    let query = postgrestFrom('collection_keywords')
      .select(
        'collectionId:collection_id,keyword:keywords!keyword_id!inner(keywordId:id,value,languageKey:language_key,keywordType:keyword_type,isAlias:is_alias,searchVolumePerMonthAvg:search_volume_per_month_avg)',
      )
      .in('collection_id', collectionIds)
      .order('collection_id', { ascending: true })
      .order('keyword_id', { ascending: true })
      .range(from, from + QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)
    const page = await runPostgrest(
      query,
      z.array(collectionKeywordRowSchema),
      'collection_keywords',
    )
    rows.push(...page)
    if (page.length < QUERY_PAGE_SIZE) return rows
  }
}

export async function fetchCollectionKeywordDetail(
  collectionId: number,
  signal?: AbortSignal,
): Promise<CollectionKeywordDetail | null> {
  const [summaries, rows] = await Promise.all([
    fetchCollectionKeywordSummaries(signal),
    fetchCollectionKeywords([collectionId], signal),
  ])
  const summary = summaries.find((row) => row.collectionId === collectionId)
  if (summary === undefined) return null
  const keywords: KeywordAssignment[] = rows.map((row) => row.keyword)
  keywords.sort((left, right) => {
    const volumeDifference =
      (right.searchVolumePerMonthAvg ?? -1) - (left.searchVolumePerMonthAvg ?? -1)
    return volumeDifference !== 0 ? volumeDifference : left.value.localeCompare(right.value, 'de')
  })
  return { summary, keywords }
}
